import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Button } from './ui/button';
import { usePublicAnalytics } from './useAnalytics';
import ProductTour from './ProductTour';
import { usePreviewCMS, EditableSection, PublishBar, cmsText } from './PreviewSiteCMS';
import {
  BarChart3, Shield, Brain, Layers, Zap, ArrowRight, Check, ChevronDown,
  Monitor, Smartphone, Globe, Lock, Bell, GitCompareArrows,
  Network, Building2, Users, FileText, Activity, Eye, Cable,
  Compass, Map, X, MessageSquare, Radar, Search, Sparkles, ClipboardCheck,
} from 'lucide-react';

// Icon registry — capabilities.items[i].icon in the CMS is a lucide icon
// name string; this map resolves it to the actual component.
const CAP_ICONS = {
  Network, BarChart3, Shield, Brain, Layers, Zap, Monitor, Smartphone,
  Globe, Lock, Bell, GitCompareArrows, Building2, Users, FileText,
  Activity, Eye, Cable, Compass, Map, MessageSquare, Radar, Search, Sparkles,
};

// Animated section wrapper
function FadeIn({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay, ease: 'easeOut' }} className={className}>
      {children}
    </motion.div>
  );
}

// Pain point stories
const STORIES = [
  {
    label: 'THE CHALLENGE',
    labelStyle: 'slate',
    pain: 'Your tech stack is a black box.',
    painDetail: 'Your VP asks "How many properties are on OPERA Cloud vs on-premise and when are the next contract renewals?" and it takes three people, two spreadsheets, and a week to get an answer nobody is 100% confident in.',
    solution: 'One interactive map. Every platform, every property.',
    solutionDetail: 'Click any property — see every technology platform deployed, who manages it, what it integrates with, and when the contract renews. Multiple visualization layouts. Instant answers.',
    screenshot: '/assets/ss_stackmap_force.jpeg',
    metric: '11',
    metricLabel: 'Visualization layouts',
  },
  {
    label: 'THE CHALLENGE',
    labelStyle: 'slate',
    pain: 'You can\'t fix what you can\'t see.',
    painDetail: 'Gaps in coverage, duplicate vendors, expired contracts, systems with no documented admin — they\'re all hiding in your portfolio. You just don\'t know where.',
    solution: '60+ reports that surface what matters.',
    solutionDetail: 'Coverage matrix, vendor risk maps, brand compliance scorecards, tech debt index, cost heatmaps — each one designed to answer a specific question your leadership team is asking.',
    screenshot: '/assets/ss_coverage_new.jpeg',
    metric: '60+',
    metricLabel: 'Reports & dashboards',
  },
  {
    label: 'THE CHALLENGE',
    labelStyle: 'slate',
    pain: 'Opening a new property takes 6 months of tech setup.',
    painDetail: 'Which platforms does it need? Which vendors? Who\'s managing what? Every new opening is a reinvention. Your best properties took years to get right — and that knowledge lives in one person\'s head.',
    solution: 'AI builds your stack for you.',
    solutionDetail: 'Describe your property — type, size, service level — and AI recommends the right platforms, estimates costs, and sets up your stack. Clone from any existing property or start from a guided wizard. What took months now takes minutes.',
    screenshot: '/assets/ss_tech_database.jpeg',
    metric: 'AI-Driven',
    metricLabel: 'Onboarding & stack recommendations',
  },
  {
    label: 'THE CHALLENGE',
    labelStyle: 'slate',
    pain: 'You\'re paying too much because you can\'t see the full picture.',
    painDetail: 'When contract renewal comes, you don\'t know your total spend with that vendor, how many properties use their products, or what alternatives exist. They have leverage. You don\'t.',
    solution: 'Vendor intelligence that shifts the balance.',
    solutionDetail: 'Deployment maps, contract summaries, overlap analysis, competitive benchmarks, ROI calculator. Walk into every negotiation knowing exactly what you spend, where, and what your options are.',
    screenshot: '/assets/ss_vendor_deployment.jpeg',
    metric: '$$$',
    metricLabel: 'Savings identified',
  },
  {
    label: 'THE CHALLENGE',
    labelStyle: 'slate',
    pain: 'Your systems don\'t talk to each other.',
    painDetail: 'The PMS isn\'t talking to the maintenance system. The payment processor isn\'t connecting to the PMS. Every property has different integrations — and often, no one knows what\'s connected and who to turn to for help. Meanwhile, vendors are releasing new integration capabilities you never hear about.',
    solution: 'Three-tier integration intelligence.',
    solutionDetail: 'Platform blueprints define what should connect. Product-level data shows what can. Property-level tracking confirms what does. When vendors release new integrations, LodgiFlow highlights the opportunity, flags the gap in reports, and notifies your team — so you never miss a chance to connect.',
    screenshot: '/assets/ss_stackmap_force.jpeg',
    metric: '3-Tier',
    metricLabel: 'Integration intelligence',
  },
  {
    label: 'THE CHALLENGE',
    labelStyle: 'slate',
    pain: 'Technology spend is scattered across a dozen invoices and nobody owns it.',
    painDetail: 'The CFO asks for a technology spend report and you\'re pulling numbers from accounting, procurement, and individual property managers. Half the invoices are miscategorized. Nobody knows the true cost per room.',
    solution: 'Financial intelligence that connects the dots.',
    solutionDetail: 'AI-powered invoice parsing, ERP connectors, spend anomaly detection, cost-per-room benchmarks, and contract-to-invoice reconciliation — all in one place. Upload a PDF and get structured data in seconds.',
    screenshot: '/assets/ss_executive.jpeg',
    metric: 'AI',
    metricLabel: 'Spend intelligence',
  },
];

const STATS = [
  { value: '100+', label: 'Features' },
  { value: '60+', label: 'Reports' },
  { value: '15', label: 'AI Assistants' },
  { value: '150+', label: 'Platform Types' },
  { value: '900+', label: 'Products' },
];

const CAPABILITIES = [
  { icon: Network, title: 'Visual Stack Mapping', desc: '11 layouts: Category, Structured, Flowchart, Org Chart, PMS Hub, Radial, Swimlanes, Logo Cluster, and Clean modes. Mobile list view.' },
  { icon: BarChart3, title: '60+ Reports', desc: 'Coverage, vendor intel, brand compliance, financials, risk — with scheduled delivery.' },
  { icon: Shield, title: 'Governance & Compliance', desc: 'Enterprise frameworks (SOC 2 / GDPR / PCI DSS / HIPAA / ISO 27001), 5-dimension governance, brand standards, audit trails.' },
  { icon: Brain, title: '15 AI Assistants', desc: 'Integration mapper, path finder, tech advisor, contract analyzer, RFP writer, spend analyst, weekly digest, 30-Day Audit Wizard.' },
  { icon: MessageSquare, title: 'Ask Your Stack', desc: 'Natural-language query bar. Type "which properties are missing a PMS?" and get instant answers backed by your live data.' },
  { icon: Radar, title: 'Vendor Watch + Score History', desc: 'Quarterly AI briefs scan public news, financial signals, and product changes — plus a sparkline of every vendor\u2019s risk score over time.' },
  { icon: Layers, title: 'Multi-Platform Products', desc: 'One product can span multiple platform types (PMS + Channel Manager + Booking Engine). The Stack Map mirrors it across every category so you see the truth once, not once per silo.' },
  { icon: ClipboardCheck, title: '30-Day Audit Wizard', desc: 'A four-week playbook — map every system, trace connections, rank gaps by dollar-risk, fix the top 3 — auto-populated from your live tenant data.' },
  { icon: Building2, title: 'Financial Intelligence', desc: 'AI spend analysis, cost benchmarks, invoice parsing, contract-to-invoice reconciliation, health scorecards.' },
  { icon: Lock, title: 'Enterprise Security', desc: 'Granular RBAC, SSO, 2FA-ready, role preview, white-label, full audit changelog.' },
  { icon: Layers, title: 'Templates & Cloning', desc: 'Pre-built onboarding templates. Clone stacks across properties. Side-by-side diff view.' },
  { icon: Activity, title: 'Operations & Tools', desc: 'System status, vendor health, SLA tracking, incidents, license utilization, asset inventory.' },
  { icon: Cable, title: '3-Tier Integrations', desc: 'Platform blueprints, product capabilities, property reality. Auto-detect gaps and opportunities.' },
  { icon: Bell, title: 'Alerts & Webhooks', desc: 'Spend anomalies, contract renewals, outages, push notifications, webhook events to Slack/Teams.' },
  { icon: Smartphone, title: 'Mobile-First PWA', desc: 'Card view default, bottom nav, offline mode, walk-through checklists, NPS feedback.' },
  { icon: Globe, title: 'Multi-Language', desc: 'English, Spanish, French, German. Auto-detect. Per-tenant settings.' },
];

export default function PreviewSite({ onNavigate }) {
  const [page, setPage] = useState('home');
  const { trackPageView, trackPublic } = usePublicAnalytics();
  const cms = usePreviewCMS();
  const bt = cms.blocksByType;

  // Tour variant selection via URL: /preview?tour=storyboard|scrollytelling|tabbed|carousel
  const [tourVariant, setTourVariant] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('tour');
      return ['storyboard', 'scrollytelling', 'tabbed', 'carousel'].includes(p) ? p : 'tabbed';
    } catch { return 'tabbed'; }
  });

  // Track page views
  useEffect(() => {
    trackPageView(`preview-${page}`);
  }, [page, trackPageView]);
  const [heroIdx, setHeroIdx] = useState(0);

  // Crossfade hero images
  useEffect(() => {
    if (page !== 'home') return;
    const timer = setInterval(() => setHeroIdx(i => (i + 1) % 2), 5000);
    return () => clearInterval(timer);
  }, [page]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#352272]" style={{ fontFamily: 'Outfit, Inter, system-ui, sans-serif' }} data-testid="preview-site-v2">
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-14 flex items-center justify-between">
          <button onClick={() => setPage('home')} className="flex items-center gap-2.5 hover:opacity-80 transition-opacity cursor-pointer">
            <img src="/assets/logo.png" alt="" className="h-8 w-8" />
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>LodgiFlow</span>
          </button>
          <div className="hidden md:flex items-center gap-8 text-sm text-[#374151]">
            <button onClick={() => setPage('why')} className="hover:text-[#352272] transition-colors">Why LodgiFlow</button>
            <button onClick={() => setPage('features')} className="hover:text-[#352272] transition-colors">Features</button>
            <a href="#pricing" onClick={() => setPage('home')} className="hover:text-[#352272] transition-colors">Plans</a>
          </div>
          <button onClick={() => onNavigate('contact')} className="bg-[#352272] text-white text-sm px-5 py-2 font-medium hover:bg-black/80 transition-colors" data-testid="hero-cta-button">
            Request Demo
          </button>
        </div>
      </nav>

      {/* WHY LODGIFLOW PAGE */}
      {page === 'why' && (
        <>
        {/* Hero */}
        <section className="py-20 sm:py-28 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#F5F3FA] via-white to-[#EBE5F7]" />
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <FadeIn>
              <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#352272]/50 mb-6">Why LodgiFlow</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Because spreadsheets<br />weren&apos;t built for this.
              </h1>
              <p className="text-lg text-[#374151] mt-6 max-w-xl leading-relaxed">
                Managing technology across dozens of properties requires a purpose-built platform — not a patchwork of spreadsheets, tribal knowledge, and hope.
              </p>
            </FadeIn>
          </div>
        </section>

        {/* The Problem */}
        <section className="border-y border-[#E5E5E5] bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <FadeIn>
                <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#352272]/40 mb-4">The Reality</p>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Multi-property technology is uniquely complex.
                </h2>
                <div className="space-y-4 text-[#374151]">
                  <p>Every property runs 15-50+ technology platforms. Across a portfolio of 30 properties, that&apos;s hundreds of vendor relationships, contracts, integrations, and support contacts.</p>
                  <p>No one person can track it all. The information lives in spreadsheets, emails, people&apos;s heads, and hope. When someone leaves, their knowledge goes with them.</p>
                  <p>LodgiFlow was built specifically for this problem — by people who lived it.</p>
                </div>
              </FadeIn>
              <FadeIn delay={0.2}>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { num: '15-50+', label: 'Platforms per property' },
                    { num: '100s', label: 'Vendor relationships' },
                    { num: '3-5', label: 'People asked for one answer' },
                    { num: '0', label: 'Single source of truth (before)' },
                  ].map(s => (
                    <div key={s.label} className="border rounded-xl p-5 text-center bg-[#F5F3FA]">
                      <div className="text-2xl font-bold text-[#352272]" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.num}</div>
                      <div className="text-xs text-[#374151] mt-1">{s.label}</div>
                    </div>
                  ))}
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* Nothing else like it */}
        <section className="py-20 sm:py-24 bg-[#352272] text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs uppercase tracking-[0.3em] font-bold text-white/40 mb-4">A New Category</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                There&apos;s nothing else like it. We checked.
              </h2>
              <p className="text-base text-white/60 max-w-2xl mb-12">The hospitality industry has PMS vendors, integration middleware, and IT asset tools. None of them solve the portfolio-wide technology management problem. Here&apos;s what exists — and what&apos;s missing.</p>
            </FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: 'PMS Vendors', examples: 'Oracle OPERA, Mews, Cloudbeds, Stayntouch', gap: 'They ARE your tech stack — they don\'t manage it. No portfolio-wide view, no vendor intelligence, no integration gap analysis across properties.' },
                { title: 'Integration Middleware', examples: 'Hapi, MuleSoft, Zapier, Workato', gap: 'They connect systems but don\'t visualize, govern, or report on the connections. No tech stack mapping, no compliance tracking, no AI advisory.' },
                { title: 'IT Asset Management', examples: 'ServiceNow, BMC Helix, OXmaint', gap: 'Built for servers and network gear, not hospitality platform stacks. No brand compliance, no vendor scorecards, no property-level tech governance.' },
                { title: 'Spreadsheets & Miro', examples: 'Excel, Google Sheets, Miro boards', gap: 'Manual, static, no live data. Falls apart at 10+ properties. No AI, no integration tracking, no automated reports. And it breaks when someone leaves.' },
              ].map((c, i) => (
                <FadeIn key={i} delay={i * 0.1}>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6 h-full">
                    <h3 className="text-sm font-bold mb-1">{c.title}</h3>
                    <p className="text-[10px] text-white/40 mb-3">{c.examples}</p>
                    <p className="text-xs text-white/70 leading-relaxed">{c.gap}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* How We're Different */}
        <section className="py-20 sm:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#352272]/40 mb-4">How We&apos;re Different</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-12" style={{ fontFamily: 'Outfit, sans-serif' }}>Purpose-built. Visual-first. AI-powered.</h2>
            </FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { title: 'Purpose-Built for Hospitality', desc: 'We understand PMS, POS, door locks, revenue management, and the 150+ platform types that hotels actually use. Generic IT tools don\'t. Our database has 900+ products from 446+ vendors, pre-categorized and AI-enriched.' },
                { title: 'Visual-First, Not Data-First', desc: 'See your entire tech stack as an interactive map — 11 layout modes plus Card View with integration indicators, swipe gestures, and a mini connection web. Not rows in a spreadsheet. Click to explore, assign, and connect.' },
                { title: '15 AI Assistants That Work', desc: 'Ask Your Stack in plain English. Get quarterly Vendor Watch briefs on every partner. Run a 30-Day Tech-Stack Audit that ranks gaps by dollar-risk. Map vendor integrations from pasted text. Analyze spend, write RFPs, parse invoices. All powered by frontier LLMs with your actual data.' },
              ].map((d, i) => (
                <FadeIn key={i} delay={i * 0.1}>
                  <div className={`p-8 rounded-xl ${i === 1 ? 'bg-[#352272] text-white' : 'bg-[#F5F3FA]'}`}>
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-40 block mb-3">0{i + 1}</span>
                    <h3 className="text-lg font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{d.title}</h3>
                    <p className={`text-sm leading-relaxed ${i === 1 ? 'text-white/70' : 'text-[#374151]'}`}>{d.desc}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* The Full Picture */}
        <section className="py-20 sm:py-24 bg-[#F5F3FA]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#352272]/40 mb-4">The Full Picture</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-12" style={{ fontFamily: 'Outfit, sans-serif' }}>Everything connects. Everything visible.</h2>
            </FadeIn>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: '11 Visualization Layouts', desc: 'Category, Structured, Flowchart, Org Chart, PMS Hub, Radial, Swimlanes, Logo Cluster, and Clean modes. Integration-weighted positioning. Sleek arrowheads. Mobile-optimized with swipe gestures.' },
                { title: '3-Tier Integration Model', desc: 'Platform blueprints define what should connect. Product data shows what can. Property tracking confirms what does. Gaps and opportunities surface automatically.' },
                { title: 'Financial Intelligence', desc: 'AI spend analysis, cost-per-room benchmarks, invoice parsing, anomaly detection, and spend vs budget tracking. All in one hub.' },
                { title: '60+ Reports & Dashboards', desc: 'Coverage matrix, vendor intelligence, brand compliance, tech debt, cost heatmaps, contract renewals — each answering a specific question leadership asks.' },
                { title: 'Enterprise Governance', desc: 'Granular RBAC permissions, SSO, 2FA-ready, brand compliance, SOC 2 / GDPR / PCI DSS / HIPAA / ISO 27001 audit frameworks, ESG tracking, white-label, and full audit trail. Multi-tenant with clean super admin separation.' },
                { title: 'Mobile-First PWA', desc: 'Card View as default mobile experience with compact list, integration flow view, connection web, category tabs, long-press menus, and swipe gestures. Push notifications for outages.' },
              ].map((f, i) => (
                <FadeIn key={i} delay={i * 0.05}>
                  <div className="bg-white rounded-xl p-6 border border-[#E5E5E5] h-full">
                    <h3 className="text-sm font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                    <p className="text-xs text-[#374151] leading-relaxed">{f.desc}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* Who It's For */}
        <section className="py-20 sm:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#352272]/40 mb-4">Who It&apos;s For</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-12" style={{ fontFamily: 'Outfit, sans-serif' }}>Built for the people who manage technology at scale.</h2>
            </FadeIn>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { role: 'VP of Technology', pain: 'Needs portfolio-wide visibility, vendor intelligence, integration gap analysis, and board-ready reports. Gets it in seconds, not weeks.' },
                { role: 'IT Directors', pain: 'Manages rollouts, integrations, governance, and vendor relationships across properties. Tracks it all in one place with 40+ reports.' },
                { role: 'Property IT Managers', pain: 'Needs support contacts, system status, and platform documentation — fast. QR support cards, mobile hub, push notifications.' },
                { role: 'CFOs & Procurement', pain: 'Wants spend visibility, cost-per-room benchmarks, contract tracking, invoice reconciliation, and negotiation leverage from AI.' },
              ].map((p, i) => (
                <FadeIn key={i} delay={i * 0.1}>
                  <div className="bg-[#F5F3FA] rounded-xl p-6 border border-[#E5E5E5]">
                    <h3 className="text-sm font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.role}</h3>
                    <p className="text-xs text-[#374151] leading-relaxed">{p.pain}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* By the Numbers */}
        <section className="py-20 sm:py-24 bg-[#352272] text-white relative overflow-hidden">
          <img src="/assets/cta_bg_tech.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <FadeIn>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-12 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>Platform at a glance</h2>
            </FadeIn>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8">
              {[
                { num: '100+', label: 'Features' }, { num: '60+', label: 'Reports' }, { num: '15', label: 'AI Assistants' },
                { num: '900+', label: 'Products' }, { num: '150+', label: 'Platform Types' }, { num: '446+', label: 'Vendors' },
              ].map(s => (
                <FadeIn key={s.label}>
                  <div className="text-center">
                    <div className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.num}</div>
                    <div className="text-xs text-white/50 mt-1 uppercase tracking-wider">{s.label}</div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 sm:py-24">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <FadeIn>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>See it with your own data.</h2>
              <p className="text-lg text-[#374151] mb-8 max-w-lg mx-auto">Request a demo and we&apos;ll load your properties, your vendors, your platforms — so you can see exactly what LodgiFlow looks like for your portfolio.</p>
              <button onClick={() => onNavigate('contact')} className="bg-[#352272] text-white px-10 py-4 text-sm font-medium hover:bg-black/80 transition-colors inline-flex items-center gap-2">
                Request a Demo <ArrowRight className="h-4 w-4" />
              </button>
            </FadeIn>
          </div>
        </section>
        </>
      )}

      {/* FEATURES INDEX PAGE */}
      {page === 'features' && (
        <section className="py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <FadeIn>
              <button onClick={() => setPage('home')} className="text-xs text-[#374151] hover:text-[#352272] mb-6 flex items-center gap-1">
                <ArrowRight className="h-3 w-3 rotate-180" /> Back to Home
              </button>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#374151] mb-4">Platform</p>
              <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Everything you need. Built to scale with you.</h1>
              <p className="text-[#374151] max-w-2xl mb-16">100+ features across 10 categories. Click any category to see the details.</p>
            </FadeIn>
            <div className="space-y-6">
              {[
                { id: 'visualization', name: 'Visualization & Mapping', desc: '11 interactive layouts. Mobile list view. Quick Fill assignments.', screenshot: '/assets/ss_stackmap_force.jpeg', count: '11 layouts' },
                { id: 'reports', name: 'Reports & Analytics', desc: '60+ reports with scheduled delivery, bulk editing, and export.', screenshot: '/assets/ss_coverage_new.jpeg', count: '60+ reports' },
                { id: 'governance', name: 'Governance & Compliance', desc: 'Enterprise audit frameworks. 5-dimension governance. Brand standards.', screenshot: '/assets/ss_governance_new.jpeg', count: 'SOC 2 / GDPR / PCI' },
                { id: 'ai', name: 'AI Intelligence', desc: '14 assistants: tech advisor, contract analyzer, path finder, property wizard.', screenshot: '/assets/ss_ai_hub.jpeg', count: '14 assistants' },
                { id: 'financial', name: 'Financial Management', desc: 'Costs, contracts, license tracking, ROI, vendor spend.', screenshot: '/assets/ss_executive.jpeg', count: '5 modules' },
                { id: 'operations', name: 'Operations & Change', desc: 'Change management, system status, onboarding, benchmarks.', screenshot: '/assets/ss_home_dashboard.jpeg', count: '8 modules' },
                { id: 'security', name: 'Security & Access', desc: 'Granular RBAC. SSO. 2FA-ready. Role preview. Full audit trail.', screenshot: '/assets/ss_admin_console.jpeg', count: 'Enterprise RBAC' },
                { id: 'config', name: 'Configuration & Setup', desc: 'Templates, cloning, bulk import, guided onboarding.', screenshot: '/assets/ss_tech_database.jpeg', count: 'AI-guided' },
                { id: 'integrations', name: 'Integrations & Tools', desc: 'Slack, webhooks, API, embeds, scheduled delivery, offline.', screenshot: '/assets/ss_vendor_deployment.jpeg', count: '11 events' },
                { id: 'mobile', name: 'Mobile & Accessibility', desc: 'Bottom nav, support hub, walk-through, 4 languages.', screenshot: '/assets/ss_wizard_new.jpeg', count: '4 languages' },
              ].map((cat, i) => (
                <FadeIn key={cat.id} delay={i * 0.04}>
                  <button onClick={() => setPage(`feature-${cat.id}`)} className="text-left w-full group">
                    <div className={`flex flex-col ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} border border-[#E5E5E5] hover:border-[#c5c5c5] transition-all hover:shadow-lg hover:shadow-black/5`}>
                      <div className="md:w-2/5 h-48 md:h-auto overflow-hidden bg-slate-50 shrink-0">
                        <img src={cat.screenshot} alt={cat.name} className="w-full h-full object-cover object-top opacity-85 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-500" loading="lazy" />
                      </div>
                      <div className="flex-1 p-8 sm:p-10 flex flex-col justify-center">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-[10px] text-[#374151] bg-[#F1F5F9] px-2.5 py-1 rounded-full font-medium">{cat.count}</span>
                        </div>
                        <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{cat.name}</h3>
                        <p className="text-base text-[#374151] mb-4">{cat.desc}</p>
                        <span className="text-sm text-[#002FA7] font-medium group-hover:underline">Explore this category →</span>
                      </div>
                    </div>
                  </button>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SAMPLE FEATURE DETAIL: Visualization */}
      {page === 'feature-visualization' && (
        <section className="py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <FadeIn>
              <button onClick={() => setPage('features')} className="text-xs text-[#374151] hover:text-[#352272] mb-8 flex items-center gap-1">
                <ArrowRight className="h-3 w-3 rotate-180" /> All Features
              </button>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#002FA7] mb-4">Visualization & Mapping</p>
              <h1 className="text-4xl sm:text-6xl font-bold tracking-tight max-w-3xl mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                See your entire tech stack. Finally.
              </h1>
              <p className="text-[#374151] max-w-xl text-xl mb-12">Eleven layouts. One click to assign. Desktop and mobile.</p>
            </FadeIn>

            {/* Hero screenshot */}
            <FadeIn delay={0.1}>
              <div className="border border-[#E5E5E5] overflow-hidden mb-20">
                <div className="h-2 bg-[#352272] flex items-center px-2 gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-white/30" /><div className="w-1.5 h-1.5 rounded-full bg-white/30" /><div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                </div>
                <img src="/assets/ss_stackmap_force.jpeg" alt="Stack Map" className="w-full" />
              </div>
            </FadeIn>

            {/* Feature blocks — alternating layout with visual separation */}
            <div className="space-y-0">
              {[
                { title: 'Eleven Layout Modes', desc: 'One click. Eleven perspectives.', screenshot: '/assets/ss_stackmap_structured.jpeg',
                  bullets: ['Category, Structured, Flowchart, Cards', 'Org Chart, PMS Hub, Radial, Swimlanes', 'Logo Cluster + Clean modes'] },
                { title: 'Mobile Stack View', desc: 'Your full stack in your pocket.', screenshot: '/assets/ss_stackmap_orgchart.jpeg',
                  bullets: ['Scrollable list grouped by category', 'Active/Gap status at a glance', 'Property switcher at the top'] },
                { title: 'Quick Fill & Editing', desc: 'See a gap. Fill it. Done.', screenshot: '/assets/ss_stackmap_flowchart.jpeg',
                  bullets: ['One-click assignment from the catalog', 'Reassign or remove from the detail panel', 'Set integration direction: → ← ↔'] },
                { title: 'Export Anywhere', desc: 'Share without a login.', screenshot: '/assets/ss_executive.jpeg',
                  bullets: ['PNG, SVG, or PDF download', 'Retina resolution for presentations', 'Keyboard shortcut for everything'] },
              ].map((feat, i) => (
                <FadeIn key={i} delay={0.1}>
                  <div className={`py-16 sm:py-20 ${i % 2 === 0 ? 'bg-[#F5F3FA]' : ''}`}
                    style={i % 2 === 0 ? { margin: '0 -24px', padding: '4rem 24px' } : {}}>
                    <div className="max-w-7xl mx-auto">
                      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center`}>
                        <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#352272]/40 mb-3 block">0{i + 1}</span>
                          <h3 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{feat.title}</h3>
                          <p className="text-lg text-[#374151] mb-6">{feat.desc}</p>
                          <ul className="space-y-3">
                            {feat.bullets.map(b => (
                              <li key={b} className="text-base text-[#374151] flex gap-3">
                                <Check className="h-5 w-5 text-[#065F46] shrink-0 mt-0.5" />{b}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
                          <div className="border border-[#E5E5E5] overflow-hidden shadow-lg shadow-black/5">
                            <div className="h-2 bg-[#352272] flex items-center px-2 gap-1">
                              <div className="w-1.5 h-1.5 rounded-full bg-white/30" /><div className="w-1.5 h-1.5 rounded-full bg-white/30" /><div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                            </div>
                            <img src={feat.screenshot} alt={feat.title} className="w-full" loading="lazy" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>

            {/* CTA */}
            <FadeIn>
              <div className="mt-28 text-center border-t border-[#E5E5E5] pt-16">
                <h3 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready to see your map?</h3>
                <p className="text-lg text-[#374151] mb-8">We'll walk you through it with your actual data.</p>
                <button onClick={() => onNavigate('contact')} className="bg-[#352272] text-white px-8 py-3 text-sm font-medium hover:bg-black/80 transition-colors inline-flex items-center gap-2">
                  Request a Demo <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </FadeIn>
          </div>
        </section>
      )}

      {/* Placeholder for other feature pages */}
      {page.startsWith('feature-') && page !== 'feature-visualization' && (() => {
        const FEATURE_DETAILS = {
          'feature-reports': {
            category: 'Reports & Analytics', headline: 'Every question your leadership asks — answered.', sub: '60+ reports. Scheduled delivery. Bulk actions.',
            hero: '/assets/ss_coverage_new.jpeg',
            blocks: [
              { title: 'Coverage Matrix', desc: 'See every gap at a glance.', screenshot: '/assets/ss_coverage.jpeg', bullets: ['Properties x platforms with green/grey status', 'Click any row to bulk-fill gaps', 'Filter by category, brand, or service level'] },
              { title: 'Executive Dashboard', desc: 'The numbers that matter.', screenshot: '/assets/ss_executive.jpeg', bullets: ['Portfolio-wide KPIs in one view', 'Maturity scorecard with weighted scoring', 'Exportable to PDF and email'] },
              { title: 'Vendor Intelligence', desc: 'Know your vendors better than they know you.', screenshot: '/assets/ss_vendor_deployment.jpeg', bullets: ['Deployment maps per vendor', 'Product adoption and overlap analysis', 'Contract summaries with renewal alerts'] },
              { title: 'Scheduled Delivery', desc: 'Reports that send themselves.', screenshot: '/assets/ss_benchmarks.jpeg', bullets: ['Daily, weekly, or monthly email delivery', 'Multiple recipients per schedule', 'Branded HTML formatting'] },
            ],
          },
          'feature-governance': {
            category: 'Governance & Compliance', headline: 'Brand standards met. Audits passed. Sleep restored.', sub: 'Enterprise frameworks. 5-dimension governance. Full audit trail.',
            hero: '/assets/ss_governance_new.jpeg',
            blocks: [
              { title: 'Brand Compliance', desc: 'Required. Approved. Not Allowed.', screenshot: '/assets/ss_compliance.jpeg', bullets: ['Per-brand designation tracking', 'Compliance % per property with drill-down', 'Brand gap detail report'] },
              { title: 'Compliance & Audit', desc: 'Enterprise frameworks, one dashboard.', screenshot: '/assets/ss_admin_console.jpeg', bullets: ['PCI DSS, SOC 2, GDPR, HIPAA, ISO 27001', 'Per-property compliance matrix', 'Verified dates and evidence URLs'] },
              { title: 'Governance Matrix', desc: 'Who owns what. Everywhere.', screenshot: '/assets/ss_governance_new.jpeg', bullets: ['5 dimensions: Admin, Credentials, Daily, Billing, Support', 'Corporate, Property, Third-Party routing', 'Bulk editing across assignments'] },
              { title: 'SSO & Credential Audit', desc: 'One login to rule them all.', screenshot: '/assets/ss_roles.jpeg', bullets: ['SSO vs standalone vs shared credentials', 'MFA tracking per platform', 'Tech debt index for ungoverned systems'] },
            ],
          },
          'feature-ai': {
            category: 'AI Intelligence', headline: '15 AI assistants that understand enterprise tech.', sub: 'From integration mapping to contract analysis to the 30-Day Audit Wizard — AI does the heavy lifting.',
            hero: '/assets/ss_ai_hub.jpeg',
            blocks: [
              { title: 'Integration Path Finder', desc: 'AI maps how your systems should connect.', screenshot: '/assets/ss_stackmap_force.jpeg', bullets: ['Select a property, get integration recommendations', 'Priority-rated with implementation sequence', 'Identifies gaps with business impact'] },
              { title: 'Tech Advisor & RFP Writer', desc: 'Recommendations and proposals in seconds.', screenshot: '/assets/ss_tech_database.jpeg', bullets: ['Property-specific platform recommendations', 'Generate complete RFPs tailored to your stack', 'AI-powered vendor comparison'] },
              { title: 'Contract Analyzer', desc: 'Extract terms from any contract.', screenshot: '/assets/ss_executive.jpeg', bullets: ['Paste text, get structured data', 'Dates, pricing, SLA terms, risk flags', 'Auto-renew clause detection'] },
              { title: 'Cost Optimizer & Benchmarks', desc: 'Find savings. Beat the average.', screenshot: '/assets/ss_benchmarks.jpeg', bullets: ['Vendor consolidation recommendations', 'Portfolio vs industry benchmarks', 'Spend pattern analysis'] },
            ],
          },
          'feature-financial': {
            category: 'Financial Management', headline: 'Technology spend — finally visible.', sub: 'AI spend analysis. ERP-ready. Cost-per-room benchmarks.',
            hero: '/assets/ss_executive.jpeg',
            blocks: [
              { title: 'Financial Intelligence Hub', desc: '9 views of your financial data.', screenshot: '/assets/ss_roi.jpeg', bullets: ['Vendor spend ranking with sparkline trends', 'AI-powered spend analysis (GPT-4o)', 'Spend anomaly detection and alerts'] },
              { title: 'Cost Benchmarks', desc: 'Know your cost per room.', screenshot: '/assets/ss_benchmarks.jpeg', bullets: ['Per Property, Per Platform, By Service Level', 'Economy vs Select vs Full-Service vs Luxury', 'Portfolio-wide cost analysis'] },
              { title: 'Invoice & Contract Management', desc: 'Upload, parse, reconcile.', screenshot: '/assets/ss_executive.jpeg', bullets: ['AI invoice parser (PDF, text, CSV)', 'Contract-to-invoice reconciliation', 'Budget vs actual tracking'] },
              { title: 'ERP Integration Ready', desc: 'Connect your financial systems.', screenshot: '/assets/ss_admin_console.jpeg', bullets: ['QuickBooks, Xero, Sage, NetSuite, SAP', 'Workday, Dynamics 365, Expensify, Concur', 'Bill.com, Coupa, Stripe, CSV import'] },
            ],
          },
          'feature-operations': {
            category: 'Operations & Change', headline: 'Rollouts tracked. Status known. Zero surprises.', sub: 'Change management, system status, benchmarks, and onboarding.',
            hero: '/assets/ss_home_dashboard.jpeg',
            blocks: [
              { title: 'Change Management', desc: 'Track every rollout.', screenshot: '/assets/ss_change_mgmt.jpeg', bullets: ['Per-property status: Scheduled, In Progress, Migrated', 'Completion % with progress bars', 'Blocker tracking and notes'] },
              { title: 'System Status', desc: 'Real-time health at a glance.', screenshot: '/assets/ss_system_status.jpeg', bullets: ['Green/Yellow/Red per platform per property', 'Incident logging with severity', 'Auto-feeds vendor health scores'] },
              { title: 'Onboarding Checklists', desc: 'Never miss a setup step.', screenshot: '/assets/ss_checklist.jpeg', bullets: ['Auto-generated by property type', 'Priority-tagged: Critical, High, Medium', 'Checkbox completion tracking'] },
              { title: 'Vendor Health & SLA', desc: 'Hold vendors accountable.', screenshot: '/assets/ss_vendor_deployment.jpeg', bullets: ['Per-vendor incident tracking', 'SLA targets vs actual performance', 'Health scoring: good, warning, critical'] },
            ],
          },
          'feature-security': {
            category: 'Security & Access', headline: 'Enterprise security. Granular RBAC. Full audit trail.', sub: 'Role-based access, SSO, 2FA-ready, and complete visibility into who changed what.',
            hero: '/assets/ss_admin_console.jpeg',
            blocks: [
              { title: 'Granular Permissions', desc: 'Control everything.', screenshot: '/assets/ss_roles.jpeg', bullets: ['14 permission categories', '4 default roles + custom roles', 'Per-report, per-action enforcement'] },
              { title: 'Enterprise SSO', desc: 'One login for everyone.', screenshot: '/assets/ss_admin_console.jpeg', bullets: ['Microsoft, Okta, Google, OneLogin, SAML/OIDC', 'Auto-provision on first login', 'Enforce SSO to block password login'] },
              { title: 'Role Preview', desc: 'See what they see.', screenshot: '/assets/ss_dashboard.jpeg', bullets: ['Preview as any role instantly', 'Verify tabs, reports, and actions', 'One-click return to admin'] },
              { title: 'Audit Trail', desc: 'Every change, documented.', screenshot: '/assets/ss_compliance.jpeg', bullets: ['Full version history', 'Filter by user, entity, date', 'Changelog with 30-day summaries'] },
            ],
          },
          'feature-config': {
            category: 'Configuration & Setup', headline: 'New property? Set up in minutes, not months.', sub: 'AI onboarding, templates, cloning, bulk import, multi-tenant.',
            hero: '/assets/ss_tech_database.jpeg',
            blocks: [
              { title: 'AI-Driven Onboarding', desc: 'Describe your property. AI builds your stack.', screenshot: '/assets/ss_wizard_new.jpeg', bullets: ['5-step guided questionnaire', 'AI recommends platforms with cost estimates', 'Gap analysis vs recommended stack'] },
              { title: 'Property Cloning', desc: 'Clone your best hotel in one click.', screenshot: '/assets/ss_dashboard.jpeg', bullets: ['Full stack clone to new property', 'Side-by-side diff view', 'Color-coded: shared, A-only, B-only'] },
              { title: 'Bulk Import', desc: 'Bring your spreadsheets.', screenshot: '/assets/ss_tech_database.jpeg', bullets: ['CSV/Excel upload wizard', 'Auto-column mapping with preview', 'Guided 3-step flow'] },
              { title: 'Multi-Tenant Architecture', desc: 'Separate everything. Control everything.', screenshot: '/assets/ss_admin_console.jpeg', bullets: ['Data isolation per management company', 'Feature flags per tenant', 'White-label branding'] },
            ],
          },
          'feature-integrations': {
            category: 'Integrations & Tools', headline: 'Connect everything. Alert everyone. Export anything.', sub: 'Slack, webhooks, API, embeds, scheduled delivery, offline PWA.',
            hero: '/assets/ss_vendor_deployment.jpeg',
            blocks: [
              { title: 'Slack & Teams Alerts', desc: 'Your team knows before the phone rings.', screenshot: '/assets/ss_system_status.jpeg', bullets: ['Push to Slack, Teams, or any webhook', 'Configure events: outages, renewals, incidents', 'One-click test before going live'] },
              { title: 'Embeddable Widgets', desc: 'LodgiFlow in your intranet.', screenshot: '/assets/ss_home_dashboard.jpeg', bullets: ['iframe embed code', 'Light and dark themes', 'KPI summary: properties, platforms, vendors'] },
              { title: 'Public API', desc: 'Build on top of LodgiFlow.', screenshot: '/assets/ss_admin_console.jpeg', bullets: ['RESTful JSON with JWT auth', 'Interactive docs', 'Custom CMDB and BI integrations'] },
              { title: 'Document Vault & Exports', desc: 'Everything stored. Everything exportable.', screenshot: '/assets/ss_executive.jpeg', bullets: ['Upload contracts, SOWs, invoices per platform', 'PNG/SVG/PDF/CSV exports', 'Branded email delivery'] },
            ],
          },
          'feature-mobile': {
            category: 'Mobile & Accessibility', headline: 'Your stack in your pocket. In four languages.', sub: 'Bottom nav, offline PWA, push notifications, walk-through checklists.',
            hero: '/assets/ss_wizard_new.jpeg',
            blocks: [
              { title: 'Mobile-First Design', desc: 'Designed for thumbs.', screenshot: '/assets/ss_support_mobile.jpeg', bullets: ['Bottom navigation: Home, Map, Reports, Support, More', 'Touch-optimized interactions', 'Responsive layouts at every breakpoint'] },
              { title: 'Push Notifications', desc: 'Know when systems go down.', screenshot: '/assets/ss_system_status.jpeg', bullets: ['Real-time alerts for outages', 'Background polling every 2 minutes', 'One-tap enable from Support hub'] },
              { title: 'Offline PWA', desc: 'Works without Wi-Fi.', screenshot: '/assets/ss_home_dashboard.jpeg', bullets: ['Service worker caches app shell', 'Last-viewed data available offline', 'iOS, Android, and desktop'] },
              { title: 'Multi-Language', desc: 'English, Spanish, French, German.', screenshot: '/assets/ss_dashboard.jpeg', bullets: ['Auto-detect browser language', 'Per-tenant default setting', 'Font size and accessibility controls'] },
            ],
          },
        };
        const detail = FEATURE_DETAILS[page];
        if (!detail) return (
          <section className="py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
              <button onClick={() => setPage('features')} className="text-xs text-[#374151] hover:text-[#352272] mb-8 flex items-center gap-1 mx-auto">
                <ArrowRight className="h-3 w-3 rotate-180" /> All Features
              </button>
              <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Coming Soon</h1>
              <p className="text-[#374151] mb-8">Detailed feature page under construction.</p>
              <button onClick={() => onNavigate('contact')} className="bg-[#352272] text-white px-8 py-3 text-sm font-medium hover:bg-black/80 transition-colors inline-flex items-center gap-2">Contact Sales <ArrowRight className="h-4 w-4" /></button>
            </div>
          </section>
        );
        return (
          <section className="py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <FadeIn>
                <button onClick={() => setPage('features')} className="text-xs text-[#374151] hover:text-[#352272] mb-8 flex items-center gap-1">
                  <ArrowRight className="h-3 w-3 rotate-180" /> All Features
                </button>
                <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#002FA7] mb-4">{detail.category}</p>
                <h1 className="text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{detail.headline}</h1>
                <p className="text-[#374151] max-w-xl text-xl mb-12">{detail.sub}</p>
              </FadeIn>
              <FadeIn delay={0.1}>
                <div className="border border-[#E5E5E5] overflow-hidden mb-8">
                  <div className="h-2 bg-[#352272] flex items-center px-2 gap-1"><div className="w-1.5 h-1.5 rounded-full bg-white/30" /><div className="w-1.5 h-1.5 rounded-full bg-white/30" /><div className="w-1.5 h-1.5 rounded-full bg-white/30" /></div>
                  <img src={detail.hero} alt={detail.category} className="w-full" />
                </div>
              </FadeIn>
              <div className="space-y-0">
                {detail.blocks.map((feat, i) => (
                  <FadeIn key={i} delay={0.1}>
                    <div className={`py-16 sm:py-20 ${i % 2 === 0 ? 'bg-[#F5F3FA]' : ''}`} style={i % 2 === 0 ? { margin: '0 -24px', padding: '4rem 24px' } : {}}>
                      <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                          <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#352272]/40 mb-3 block">0{i + 1}</span>
                            <h3 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{feat.title}</h3>
                            <p className="text-lg text-[#374151] mb-6">{feat.desc}</p>
                            <ul className="space-y-3">
                              {feat.bullets.map(b => (<li key={b} className="text-base text-[#374151] flex gap-3"><Check className="h-5 w-5 text-[#065F46] shrink-0 mt-0.5" />{b}</li>))}
                            </ul>
                          </div>
                          <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
                            <div className="border border-[#E5E5E5] overflow-hidden shadow-lg shadow-black/5">
                              <div className="h-2 bg-[#352272] flex items-center px-2 gap-1"><div className="w-1.5 h-1.5 rounded-full bg-white/30" /><div className="w-1.5 h-1.5 rounded-full bg-white/30" /><div className="w-1.5 h-1.5 rounded-full bg-white/30" /></div>
                              <img src={feat.screenshot} alt={feat.title} className="w-full" loading="lazy" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </FadeIn>
                ))}
              </div>
              <FadeIn>
                <div className="mt-20 text-center border-t border-[#E5E5E5] pt-16">
                  <h3 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready to leave the guesswork behind?</h3>
                  <p className="text-lg text-[#374151] mb-8">We&apos;ll walk you through it with your actual data.</p>
                  <button onClick={() => onNavigate('contact')} className="bg-[#352272] text-white px-8 py-3 text-sm font-medium hover:bg-black/80 transition-colors inline-flex items-center gap-2">Request a Demo <ArrowRight className="h-4 w-4" /></button>
                </div>
              </FadeIn>
            </div>
          </section>
        );
      })()}

      {/* HOME PAGE CONTENT */}
      {page === 'home' && (
      <>
      {/* Hero with crossfade images */}
      <EditableSection block={bt.hero} isSuperAdmin={cms.isSuperAdmin} token={cms.token} onSave={cms.saveBlockDraft} label="Hero">
      <section className="relative overflow-hidden">
        {/* Crossfading background images */}
        <div className="absolute inset-0 z-0">
          <img src={cmsText(bt.hero, 'background', '/assets/hero_desk_chaos.png')} alt="" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-in-out" style={{ opacity: heroIdx === 0 ? 1 : 0 }} />
          <img src={cmsText(bt.hero, 'background_alt', '/assets/hero_ecosystem.png')} alt="" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-in-out" style={{ opacity: heroIdx === 1 ? 1 : 0 }} />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/40" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 sm:py-28 lg:py-36 relative z-10">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#374151] mb-6">{cmsText(bt.hero, 'eyebrow', 'Enterprise Technology Stack Management')}</p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h1
              className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[1.05] max-w-4xl"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              dangerouslySetInnerHTML={{
                __html: cmsText(bt.hero, 'headline_html', "Managing your tech stack<br />shouldn't be an&nbsp;<span class='italic text-[#352272]'>adventure</span>."),
              }}
            />
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="text-lg sm:text-xl text-[#374151] mt-6 max-w-xl leading-relaxed">
              {cmsText(bt.hero, 'subhead', "Every renewal, integration, and rollout shouldn't feel like exploring uncharted territory. LodgiFlow gives you a single map of every platform across every property — no surprises, no dead ends.")}
            </p>
          </FadeIn>
          <FadeIn delay={0.3}>
            <div className="flex flex-col sm:flex-row gap-3 mt-10">
              <button onClick={() => onNavigate('contact')} className="bg-[#352272] text-white px-8 py-3.5 text-sm font-medium hover:bg-black/80 transition-colors flex items-center gap-2">
                {cmsText(bt.hero, 'primary_cta', { label: 'Request a Demo' }).label || 'Request a Demo'} <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => setPage('features')} className="border border-[#352272] text-[#352272] px-8 py-3.5 text-sm font-medium hover:bg-[#352272] hover:text-white transition-colors">
                {cmsText(bt.hero, 'secondary_cta', { label: 'Explore the Platform' }).label || 'Explore the Platform'}
              </button>
            </div>
          </FadeIn>

          {/* Image indicator dots */}
          <div className="flex gap-2 mt-8">
            {[0, 1].map(i => (
              <button key={i} onClick={() => setHeroIdx(i)} className={`w-2 h-2 rounded-full transition-all ${heroIdx === i ? 'bg-[#352272] w-6' : 'bg-[#352272]/20'}`} />
            ))}
          </div>
        </div>
      </section>
      </EditableSection>

      {/* Before/After — dramatic side-by-side cards */}
      <EditableSection block={bt.before_after} isSuperAdmin={cms.isSuperAdmin} token={cms.token} onSave={cms.saveBlockDraft} label="Before / After">
      <section className="relative bg-gradient-to-b from-[#FAFAFA] via-white to-[#FAFAFA] overflow-hidden">
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #352272 0%, transparent 40%), radial-gradient(circle at 80% 70%, #6B52AE 0%, transparent 40%)' }} />

        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-20 sm:py-24 relative">
          <FadeIn>
            <div className="text-center mb-14">
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#352272]/60">{cmsText(bt.before_after, 'kicker', 'The transformation')}</span>
              <h2
                className="text-3xl sm:text-4xl font-bold tracking-tight mt-3"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                dangerouslySetInnerHTML={{
                  __html: cmsText(bt.before_after, 'headline_html', "From <span class='italic text-[#991B1B]'>expedition</span> to <span class='italic text-[#065F46]'>control room</span>."),
                }}
              />
            </div>
          </FadeIn>

          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10 items-stretch">
            {/* BEFORE card */}
            <FadeIn>
              <div className="relative rounded-2xl overflow-hidden bg-white border border-[#F3D6D6] transition-all hover:-translate-y-1 hover:shadow-2xl" style={{ boxShadow: '0 12px 40px -12px rgba(153,27,27,0.15), 0 4px 12px -4px rgba(0,0,0,0.05)' }} data-testid="before-card">
                <div className="absolute inset-x-0 top-0 h-1" style={{ background: 'linear-gradient(90deg, #DC2626, #991B1B, #DC2626)' }} />
                <div className="p-8 sm:p-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-[#FEF2F2]">
                      <Compass className="h-5 w-5 text-[#991B1B]" style={{ transform: 'rotate(-25deg)' }} />
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#991B1B]">{cmsText(bt.before_after, 'before', {}).label || 'Before'}</span>
                      <div className="text-sm text-[#374151] font-semibold">{cmsText(bt.before_after, 'before', {}).subtitle || 'Off-trail. No map.'}</div>
                    </div>
                  </div>
                  <ul className="space-y-4">
                    {(cmsText(bt.before_after, 'before', {}).items || [
                      { title: '50+ platforms nobody can see', sub: 'Institutional knowledge lives in departing employees\' heads.' },
                      { title: 'Contracts renewed by accident', sub: 'Auto-renewals hit inbox spam folders and pass unnoticed.' },
                      { title: 'No idea who manages what', sub: 'A ticket bounces between three teams before anyone owns it.' },
                    ]).map((item, idx) => (
                      <li key={item.title || idx} className="flex gap-3">
                        <div className="shrink-0 mt-0.5 h-5 w-5 rounded-full bg-[#FEF2F2] flex items-center justify-center">
                          <X className="h-3 w-3 text-[#991B1B]" strokeWidth={3} />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-[#0F172A]">{item.title}</div>
                          <div className="text-xs text-[#6B7280] mt-0.5">{item.sub}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </FadeIn>

            {/* VS divider */}
            <div className="absolute hidden md:flex left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-12 rounded-full items-center justify-center z-10" style={{ background: 'linear-gradient(135deg, #352272, #6B52AE)', boxShadow: '0 8px 24px -6px rgba(53,34,114,0.4)' }}>
              <span className="text-white text-[10px] font-bold tracking-widest">VS</span>
            </div>

            {/* AFTER card */}
            <FadeIn delay={0.1}>
              <div className="relative rounded-2xl overflow-hidden bg-white border border-[#D6F3E5] transition-all hover:-translate-y-1 hover:shadow-2xl" style={{ boxShadow: '0 12px 40px -12px rgba(6,95,70,0.15), 0 4px 12px -4px rgba(0,0,0,0.05)' }} data-testid="after-card">
                <div className="absolute inset-x-0 top-0 h-1" style={{ background: 'linear-gradient(90deg, #34D399, #065F46, #34D399)' }} />
                <div className="p-8 sm:p-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-[#ECFDF5]">
                      <Map className="h-5 w-5 text-[#065F46]" />
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#065F46]">{cmsText(bt.before_after, 'after', {}).label || 'After'}</span>
                      <div className="text-sm text-[#374151] font-semibold">{cmsText(bt.before_after, 'after', {}).subtitle || 'Charted. Guided. Under control.'}</div>
                    </div>
                  </div>
                  <ul className="space-y-4">
                    {(cmsText(bt.before_after, 'after', {}).items || [
                      { title: 'Interactive map of every platform', sub: 'One click, one click, and you\'re looking at the whole portfolio.' },
                      { title: 'Vendor intelligence for negotiations', sub: 'Deployment counts, spend benchmarks, and renewal timelines at your fingertips.' },
                      { title: 'Clone a property stack in one click', sub: 'New opening? Duplicate a proven stack in seconds, not months.' },
                    ]).map((item, idx) => (
                      <li key={item.title || idx} className="flex gap-3">
                        <div className="shrink-0 mt-0.5 h-5 w-5 rounded-full bg-[#ECFDF5] flex items-center justify-center">
                          <Check className="h-3 w-3 text-[#065F46]" strokeWidth={3} />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-[#0F172A]">{item.title}</div>
                          <div className="text-xs text-[#6B7280] mt-0.5">{item.sub}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
      </EditableSection>

      {/* Stats Strip */}
      <EditableSection block={bt.stats} isSuperAdmin={cms.isSuperAdmin} token={cms.token} onSave={cms.saveBlockDraft} label="Stats">
      <section className="border-y border-[#E5E5E5] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 grid grid-cols-2 sm:grid-cols-5 gap-8">
          {(cmsText(bt.stats, 'items', STATS) || STATS).map((s, i) => (
            <FadeIn key={s.label || i}>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.value}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-[#374151] mt-1">{s.label}</div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>
      </EditableSection>

      {/* Pain Point Stories */}
      <section id="stories" className="py-0">
        {/* Bold section header with dark background bar */}
        <div className="bg-[#352272] py-10 sm:py-12 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #7C3AED 0%, transparent 50%), radial-gradient(circle at 80% 50%, #4F46E5 0%, transparent 50%)' }} />
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <FadeIn>
              <p className="text-xs uppercase tracking-[0.3em] font-bold text-white/40 mb-3">The map is missing</p>
              <div className="flex flex-col lg:flex-row lg:items-end lg:gap-12">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Every hospitality tech team<br />hits the same six switchbacks.
                </h2>
                <p className="text-sm sm:text-base text-white/60 max-w-md mt-3 lg:mt-0 lg:pb-1">Every one has the same shape: no map, no compass, no confidence about what's around the next corner. Here's how LodgiFlow gets you back on the trail.</p>
              </div>
            </FadeIn>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20">
          <div className="space-y-32">
            {STORIES.map((story, i) => (
              <div key={i} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${i % 2 === 1 ? 'lg:direction-rtl' : ''}`}>
                <FadeIn className={i % 2 === 1 ? 'lg:order-2' : ''}>
                  <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-4 py-1 mb-4">{story.label}</span>
                  <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{story.pain}</h3>
                  <p className="text-[#374151] mb-8 leading-relaxed">{story.painDetail}</p>

                  <div className="border-l-2 border-[#065F46] pl-6">
                    <span className="inline-block text-xs uppercase tracking-[0.2em] font-bold text-white bg-[#065F46] px-3 py-1 mb-3">THE SOLUTION</span>
                    <h4 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{story.solution}</h4>
                    <p className="text-[#374151] text-sm leading-relaxed">{story.solutionDetail}</p>
                  </div>

                  <div className="mt-8 inline-flex items-center gap-3 border border-[#E5E5E5] px-5 py-3">
                    <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{story.metric}</span>
                    <span className="text-xs uppercase tracking-[0.1em] text-[#374151]">{story.metricLabel}</span>
                  </div>
                </FadeIn>

                <FadeIn delay={0.2} className={i % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="border border-[#E5E5E5] overflow-hidden bg-white">
                    <div className="h-2 bg-[#352272] flex items-center px-2 gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                      <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                      <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                    </div>
                    <img src={story.screenshot} alt={story.solution} className="w-full" loading="lazy" />
                  </div>
                </FadeIn>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Tour — variant switchable via ?tour=storyboard|scrollytelling|tabbed|carousel */}
      <EditableSection block={bt.tour} isSuperAdmin={cms.isSuperAdmin} token={cms.token} onSave={cms.saveBlockDraft} label="Product Tour">
      <div>
        {(cmsText(bt.tour, 'kicker', '') || cmsText(bt.tour, 'headline_html', '') || cmsText(bt.tour, 'intro', '')) && (
          <section className="pt-20 sm:pt-24 pb-2 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              {cmsText(bt.tour, 'kicker', '') && (
                <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#374151] mb-4" data-testid="tour-kicker">{cmsText(bt.tour, 'kicker', '')}</p>
              )}
              {cmsText(bt.tour, 'headline_html', '') && (
                <h2
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl mb-4"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                  dangerouslySetInnerHTML={{ __html: cmsText(bt.tour, 'headline_html', '') }}
                  data-testid="tour-headline"
                />
              )}
              {cmsText(bt.tour, 'intro', '') && (
                <p className="text-[#374151] max-w-2xl text-lg" data-testid="tour-intro">{cmsText(bt.tour, 'intro', '')}</p>
              )}
            </div>
          </section>
        )}
        <ProductTour variant={cmsText(bt.tour, 'variant', tourVariant) || tourVariant} chapters={cmsText(bt.tour, 'chapters', null)} />
      </div>
      </EditableSection>

      {/* Dev-only tour variant switcher — visible when ?tour_debug=1 in the URL */}
      {typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('tour_debug') === '1' && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-16 z-[9997] flex gap-1 p-1 rounded-full border shadow-2xl" style={{ background: 'rgba(255,255,255,0.95)', borderColor: '#E5E5E5', backdropFilter: 'blur(8px)' }} data-testid="tour-variant-switcher">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold text-[#374151] select-none">Tour</div>
          {[
            { key: 'storyboard', label: 'A1 · Storyboard' },
            { key: 'scrollytelling', label: 'A2 · Scrolly' },
            { key: 'tabbed', label: 'A3 · Tabbed' },
            { key: 'carousel', label: 'A4 · Carousel' },
          ].map((opt) => {
            const isActive = opt.key === tourVariant;
            return (
              <a
                key={opt.key}
                href={`/preview?tour=${opt.key}&tour_debug=1`}
                data-testid={`tour-variant-${opt.key}`}
                className="px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors"
                style={{
                  background: isActive ? '#352272' : 'transparent',
                  color: isActive ? 'white' : '#352272',
                  textDecoration: 'none',
                }}
              >
                {opt.label}
              </a>
            );
          })}
        </div>
      )}

      {/* Capabilities Grid */}
      <EditableSection block={bt.capabilities} isSuperAdmin={cms.isSuperAdmin} token={cms.token} onSave={cms.saveBlockDraft} label="Capabilities">
      <section id="capabilities" className="py-24 sm:py-32 bg-white border-y border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#374151] mb-4">{cmsText(bt.capabilities, 'kicker', 'The Platform · Your compass')}</p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-2xl mb-16"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              dangerouslySetInnerHTML={{
                __html: cmsText(bt.capabilities, 'headline_html', "Everything you need<br />to stop navigating <span class='italic text-[#352272]'>blind</span>."),
              }}
            />
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#E5E5E5]">
            {(cmsText(bt.capabilities, 'items', CAPABILITIES) || CAPABILITIES).map((cap, i) => {
              const Icon = typeof cap.icon === 'function' ? cap.icon : (CAP_ICONS[cap.icon] || Network);
              return (
                <FadeIn key={cap.title || i} delay={i * 0.05}>
                  <div className="p-8 border-b border-r border-[#E5E5E5] hover:bg-[#FAFAFA] transition-colors" data-testid={`capability-${i}`}>
                    <Icon className="h-5 w-5 mb-4 text-[#002FA7]" />
                    <h3 className="text-sm font-bold mb-2">{cap.title}</h3>
                    <p className="text-sm text-[#374151] leading-relaxed">{cap.desc}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>
      </EditableSection>

      {/* Contact Sales */}
      <EditableSection block={bt.pricing} isSuperAdmin={cms.isSuperAdmin} token={cms.token} onSave={cms.saveBlockDraft} label="Pricing">
      <section id="pricing" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#374151] mb-4">{cmsText(bt.pricing, 'kicker', 'Chart your course')}</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{cmsText(bt.pricing, 'headline', 'Flexible plans that scale with your portfolio.')}</h2>
            <p className="text-[#374151] mb-10 max-w-lg">{cmsText(bt.pricing, 'subhead', 'Per-room-per-month pricing with volume discounts. Custom packages for enterprise organizations. Start where you are — grow at your own pace.')}</p>
          </FadeIn>
          <FadeIn delay={0.1}>
            {(() => {
              const PRICING_DEFAULTS = [
                { name: 'Core', desc: 'Visibility & essentials', theme: 'light-lavender', bullets: ['Interactive stack map (11 layouts)', 'Platform & vendor database', 'Essential reports + exports', 'Property profiles'], cta_label: 'Contact Sales' },
                { name: 'Plus', desc: 'Governance & analytics', theme: 'lavender', bullets: ['Everything in Core', 'All 60+ reports + CSV', 'Brand compliance & governance', 'Contract management', 'QR support cards'], cta_label: 'Contact Sales' },
                { name: 'Professional', desc: 'AI, ops & financial intel', theme: 'purple', bullets: ['Everything in Plus', '15 AI assistants + Ask Your Stack', 'Financial Intelligence Hub', 'Vendor Watch quarterly briefs + score history', '30-Day Tech-Stack Audit Wizard', 'Change management'], cta_label: 'Contact Sales' },
                { name: 'Enterprise', desc: 'SSO, white-label, API', theme: 'deep-purple', bullets: ['Everything in Professional', 'Enterprise SSO & 2FA-ready', 'White-label branding', 'ERP integrations', 'Public API & webhooks'], cta_label: 'Contact Sales' },
              ];
              const THEME_STYLES = {
                'light-lavender': { bg: 'bg-[#F5F3FA]', text: 'text-[#374151]', check: 'text-[#065F46]', isLight: false, btnClass: 'border border-[#352272] hover:bg-[#352272] hover:text-white' },
                'lavender': { bg: 'bg-[#EBE5F7]', text: 'text-[#374151]', check: 'text-[#065F46]', isLight: false, btnClass: 'border border-[#352272] hover:bg-[#352272] hover:text-white' },
                'purple': { bg: 'bg-[#6B52AE]', text: 'text-white/70', check: 'text-white/40', isLight: true, btnClass: 'bg-white text-[#352272] hover:bg-white/90' },
                'deep-purple': { bg: 'bg-[#352272]', text: 'text-white/70', check: 'text-white/40', isLight: true, btnClass: 'bg-white text-[#352272] hover:bg-white/90' },
              };
              const tiers = cmsText(bt.pricing, 'tiers', PRICING_DEFAULTS) || PRICING_DEFAULTS;
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#E5E5E5] max-w-5xl">
                  {tiers.map((plan, i, arr) => {
                    const style = THEME_STYLES[plan.theme] || THEME_STYLES['light-lavender'];
                    const bullets = Array.isArray(plan.bullets) ? plan.bullets : [];
                    return (
                      <FadeIn key={plan.name || i} delay={i * 0.1}>
                        <div className={`p-6 lg:p-8 border-b lg:border-b-0 ${i < arr.length - 1 ? 'lg:border-r' : ''} border-[#E5E5E5] ${style.bg} h-full flex flex-col`}>
                          <h3 className={`text-lg font-bold mb-1 ${style.isLight ? 'text-white' : ''}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{plan.name}</h3>
                          <p className={`text-sm mb-6 ${style.isLight ? 'text-white/50' : 'text-[#374151]'}`}>{plan.desc}</p>
                          <ul className="space-y-2 mb-6 flex-1">
                            {bullets.map((b, bi) => (
                              <li key={bi} className={`text-sm flex gap-2 ${style.text}`}>
                                <Check className={`h-4 w-4 shrink-0 mt-0.5 ${style.check}`} />{b}
                              </li>
                            ))}
                          </ul>
                          <button onClick={() => onNavigate('contact')}
                            className={`w-full py-2.5 text-sm font-medium transition-colors mt-auto ${style.btnClass}`}>
                            {plan.cta_label || 'Contact Sales'}
                          </button>
                        </div>
                      </FadeIn>
                    );
                  })}
                </div>
              );
            })()}
          </FadeIn>
        </div>
      </section>
      </EditableSection>

      {/* Final CTA */}
      <EditableSection block={bt.cta} isSuperAdmin={cms.isSuperAdmin} token={cms.token} onSave={cms.saveBlockDraft} label="Call to Action">
      <section className="relative py-24 sm:py-32 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[#1a1035]" />
        <img src={cmsText(bt.cta, 'background', '/assets/cta_bg_tech.jpg')} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-l from-[#1a1035] via-[#1a1035]/70 to-transparent" />
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <FadeIn>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              dangerouslySetInnerHTML={{
                __html: cmsText(bt.cta, 'headline_html', "Ready to trade the <span class='italic'>expedition</span> for a map?"),
              }}
            />
            <p className="text-white/60 text-lg mb-10 max-w-lg mx-auto">
              {cmsText(bt.cta, 'subhead', "Request a demo and we'll walk your portfolio, top to bottom — no dead ends, no detours.")}
            </p>
            <button onClick={() => onNavigate('contact')} className="bg-white text-[#352272] px-10 py-4 text-sm font-medium hover:bg-white/90 transition-colors inline-flex items-center gap-2">
              {cmsText(bt.cta, 'button', { label: 'Request a Demo' }).label || 'Request a Demo'} <ArrowRight className="h-4 w-4" />
            </button>
          </FadeIn>
        </div>
      </section>
      </EditableSection>
      </>
      )}

      {/* Footer */}
      <footer className="border-t border-[#E5E5E5] py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <img src="/assets/logo.png" alt="" className="h-4 w-4" />
            <span className="text-xs font-bold">LodgiFlow</span>
          </div>
          <p className="text-xs text-[#374151]">Enterprise technology stack management for multi-property organizations.</p>
          <button onClick={() => onNavigate('landing')} className="text-xs text-[#374151] hover:text-[#352272]">Main Site</button>
        </div>
      </footer>

      {/* Floating publish bar — super-admins only, when they have unpublished CMS draft changes */}
      {cms.isSuperAdmin && (
        <PublishBar
          pageMeta={cms.pageMeta}
          onPublish={cms.publish}
          onDiscard={cms.discard}
          onNavigate={() => { window.location.href = '/app?tab=super-admin&section=marketing-cms'; }}
        />
      )}
    </div>
  );
}
